import axios, { AxiosInstance } from "axios";
import { useAuth } from "@clerk/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SITE_PASS_KEY = "kmt_site_pass";
export const publicApi = axios.create({ baseURL: API });
publicApi.interceptors.request.use((cfg) => {
  const p = localStorage.getItem(SITE_PASS_KEY);
  if (p) cfg.headers["X-Site-Pass"] = p;
  return cfg;
});

export function useOwnerApi(): AxiosInstance {
  const { getToken } = useAuth();
  const ref = useRef(getToken);
  ref.current = getToken;
  return useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use(async (cfg) => {
      const t = await ref.current();
      if (t) cfg.headers.Authorization = `Bearer ${t}`;
      return cfg;
    });
    return inst;
  }, []);
}

export function errMsg(e: any): string {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: any) => `${x.loc?.slice(-1)[0] ?? ""}: ${x.msg}`).join("; ");
  return d || e?.message || "Something went wrong";
}

export function useLoad<T = any>(fn: () => Promise<T>, deps: any[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fn());
      setError("");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, setData, error, loading, reload };
}

export async function downloadFile(api: AxiosInstance, url: string, filename: string) {
  const res = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
  return res;
}
